/** Viewport-fixed overlay position for compact filter icon popovers. */

const DEFAULT_GAP_PX = 4;
const DEFAULT_MARGIN_PX = 8;
/** Fallback when panel is not laid out yet (≈ 16rem list + title/search/footer). */
const DEFAULT_PANEL_HEIGHT_PX = 280;

/**
 * Places a filter panel under the trigger when there is room; otherwise opens upward
 * (unless `preferBelow` — used for top-toolbar HP/Dig. so the panel always drops down).
 * `preferAbove` always opens upward, even when that leaves the viewport — used by the
 * floor-apartment column filter so the panel does not cover the rows.
 * Recalculate on open / scroll / resize. Horizontal edges stay in the viewport.
 */
export function filterOverlayPanelStyle(
  trigger: HTMLElement,
  panel: HTMLElement | undefined,
  minWidthPx = 288,
  options?: {
    gapPx?: number;
    marginPx?: number;
    /** Used only before the panel has a measurable height. */
    estimatedHeightPx?: number;
    /** Prefer opening below the trigger (skip upward flip). */
    preferBelow?: boolean;
    /** Always open above the trigger. Do not pull the panel down over the rows. */
    preferAbove?: boolean;
  },
): { [key: string]: string } {
  const gap = options?.gapPx ?? DEFAULT_GAP_PX;
  const margin = options?.marginPx ?? DEFAULT_MARGIN_PX;
  const rect = trigger.getBoundingClientRect();
  const panelW = panel?.offsetWidth || Math.max(rect.width, minWidthPx);
  const measuredH = panel?.offsetHeight ?? 0;
  const panelH = measuredH > 0 ? measuredH : (options?.estimatedHeightPx ?? DEFAULT_PANEL_HEIGHT_PX);

  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;

  let left = rect.left;
  if (left + panelW > viewportW - margin) {
    left = Math.max(margin, viewportW - margin - panelW);
  }
  left = Math.max(margin, Math.min(left, viewportW - margin - Math.min(panelW, viewportW - 2 * margin)));

  const spaceBelow = viewportH - rect.bottom - margin;
  const spaceAbove = rect.top - margin;
  const openUpward =
    !!options?.preferAbove
    || (
      !options?.preferBelow
      && spaceBelow < panelH
      && spaceAbove > spaceBelow
    );

  let top: number;
  if (openUpward) {
    top = rect.top - gap - panelH;
  } else {
    top = rect.bottom + gap;
  }

  // Keep the panel inside the viewport (clamp). Prefer the chosen side; then squeeze.
  // preferBelow (toolbar HP/Dig.) must stay under the icon — a tall measurement must not
  // pull `top` above the trigger (that painted the popover over the toolbar).
  // preferAbove stays above the trigger even if the panel leaves the viewport.
  if (!options?.preferAbove && options?.preferBelow) {
    if (top < margin) top = margin;
  } else if (!options?.preferAbove) {
    const maxTop = Math.max(margin, viewportH - margin - panelH);
    if (top < margin) top = margin;
    if (top > maxTop) top = maxTop;
  }

  return {
    position: 'fixed',
    top: `${Math.round(top)}px`,
    left: `${Math.round(left)}px`,
    'min-width': `${Math.round(Math.max(rect.width, minWidthPx))}px`,
  };
}

/** Whether the panel should open above the trigger for the given geometry. */
export function filterOverlayOpensUpward(
  triggerRect: Pick<DOMRect, 'top' | 'bottom'>,
  panelHeightPx: number,
  viewportHeightPx = typeof window !== 'undefined' ? window.innerHeight : 0,
  marginPx = DEFAULT_MARGIN_PX,
): boolean {
  const spaceBelow = viewportHeightPx - triggerRect.bottom - marginPx;
  const spaceAbove = triggerRect.top - marginPx;
  return spaceBelow < panelHeightPx && spaceAbove > spaceBelow;
}
